import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Bar, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
  ZIndexLayer, usePlotArea, useXAxisScale, useYAxisScale,
} from 'recharts';
import { addDays, parseISODate, shortDate, toISODate, today } from '../lib/dates';
import { LOCALE, grams, kcal } from '../lib/format';
import { useApp, useAppPath } from '../lib/AppContext';
import { AnnouncementBanner } from '../components/Announcements';
import { TrendsSkeleton } from '../components/Skeletons';

const RANGES = [
  { id: '7D', label: '7 ngày', days: 7 },
  { id: '30D', label: '30 ngày', days: 30 },
  { id: '90D', label: '90 ngày', days: 90 },
  { id: '6M', label: '6 tháng', months: 6 },
  { id: '12M', label: '12 tháng', months: 12 },
];

const rangeParams = (range) => {
  const to = today();
  if (range.days) return { from: addDays(to, -(range.days - 1)), to, group: 'day' };
  const start = parseISODate(to);
  start.setDate(1);
  start.setMonth(start.getMonth() - (range.months - 1));
  return { from: toISODate(start), to, group: 'month' };
};

const SERIES = {
  calories_in: { label: 'Đã ăn', color: 'var(--viz-in)', format: (v) => `${kcal(v)} kcal` },
  calories_out: { label: 'Đã đốt', color: 'var(--viz-out)', format: (v) => `${kcal(v)} kcal` },
  protein_g: { label: 'Chất đạm', color: 'var(--viz-protein)', format: grams },
};

const axisProps = {
  axisLine: false,
  tickLine: false,
  tick: { fill: 'var(--viz-axis)', fontSize: 11 },
};

function ChartTooltip({ active, payload, label, group }) {
  if (!active || !payload?.length) return null;
  // Goal lines are drawn as series too; the tooltip lists only the measured values.
  payload = payload.filter((p) => SERIES[p.dataKey]);
  if (!payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-box border border-base-300 bg-base-100 px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-semibold">{formatPeriod(label, group, true)}</div>
      {!row.logged ? (
        <div className="text-muted">Chưa ghi gì</div>
      ) : (
        payload.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2">
            <span className="inline-block size-2.5 rounded-full" style={{ background: SERIES[p.dataKey].color }} />
            <span className="text-muted">{SERIES[p.dataKey].label}</span>
            <span className="tabular ml-auto font-semibold">{SERIES[p.dataKey].format(p.value)}</span>
          </div>
        ))
      )}
      {group === 'month' && row.logged && (
        <div className="mt-1 text-muted">Trung bình mỗi ngày · đã ghi {row.days_logged} ngày</div>
      )}
    </div>
  );
}

// A day (or a month's daily average) is within its target when eaten − burned stays under that day's
// calories: the same rule as the Today ring, and always against the targets in force that day.
const withinTarget = (row) => row.calories_in - row.calories_out <= row.calorie_goal;

function CalorieTooltip({ active, payload, label, group }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  // Đã ăn − Vận động = Calo ròng, like the Today card; Vận động only on days with exercise.
  const lines = [['Đã ăn', row.calories_in], row.calories_out > 0 && ['Vận động', row.calories_out]].filter(Boolean);
  return (
    <div className="rounded-box border border-base-300 bg-base-100 px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-semibold">{formatPeriod(label, group, true)}</div>
      {!row.logged ? <div className="text-muted">Chưa ghi gì</div> : (
        <>
          {lines.map(([name, v]) => (
            <div key={name} className="flex items-center gap-3">
              <span className="text-muted">{name}</span>
              <span className="tabular ml-auto font-semibold">{kcal(v)} kcal</span>
            </div>
          ))}
          <div className="mt-1 flex items-center gap-3 border-t border-line pt-1">
            <span className="font-semibold">Calo ròng</span>
            <span className="tabular ml-auto font-extrabold">{kcal(row.net)} kcal</span>
          </div>
        </>
      )}
      {row.logged && row.calorie_goal && (
        <div className="mt-1">
          <span className={`font-semibold ${withinTarget(row) ? 'text-brand-text' : 'text-[#c2410c]'}`}>
            {withinTarget(row) ? 'Trong mục tiêu' : 'Vượt mục tiêu'}
          </span>
          <span className="tabular text-muted"> · Mục tiêu ngày này {kcal(row.calorie_goal)}</span>
        </div>
      )}
      {group === 'month' && row.logged && <div className="mt-1 text-muted">Trung bình mỗi ngày · đã ghi {row.days_logged} ngày</div>}
    </div>
  );
}

// Pixel centre of a period's band (bars and the step line's corners sit there): the axis scale gives the
// band's left edge, and bands are as wide as the distance between two neighbours.
const useBandCenter = (periods) => {
  const xScale = useXAxisScale();
  if (!xScale || periods.length < 2) return null;
  const half = (xScale(periods[1]) - xScale(periods[0])) / 2;
  return (value) => xScale(value) + half;
};
// Markers closer than this merge into one numbered dot.
const MERGE_PX = 60;

// The server's target changes, as markers for one chart: `from`/`to` place the ring on that chart's line,
// `text` is what the details card says (`lines` when it takes several rows).
const calorieMarks = (changes) => changes.filter((c) => c.calorie_goal).map((c) => ({
  date: c.date, ...c.calorie_goal,
  text: `${shortDate(c.date)} · ${kcal(c.calorie_goal.from)} → ${kcal(c.calorie_goal.to)} kcal`,
}));
const proteinMarks = (changes) => changes.filter((c) => c.protein_goal || c.workout_protein_goal).map((c) => {
  const value = (v) => (v == null ? '–' : grams(v));
  const day = shortDate(c.date);
  const lines = [
    c.protein_goal && `${day} · ngày nghỉ ${value(c.protein_goal.from)} → ${value(c.protein_goal.to)}`,
    c.workout_protein_goal && `${day} · ngày tập ${value(c.workout_protein_goal.from)} → ${value(c.workout_protein_goal.to)}`,
  ].filter(Boolean);
  // The ring sits on the rest-day line's step, or the gym-day line's when only that one changed.
  const line = c.protein_goal || c.workout_protein_goal;
  return { date: c.date, from: line.from, to: line.to, text: lines.join(', '), lines };
});

// Target changes on the goal line, with no text on the chart: a hollow ring in the middle of each step (on
// a dashed line through the plot), or one numbered dot above the plot where changes sit close together.
// Tapping one opens its details (see ChangeDetails). With `goal`, the current target's value sits at the
// line's right end, on the side away from the last step so the two never collide.
function ChangeMarkers({ periods, changes, goal, onOpen }) {
  const center = useBandCenter(periods);
  const yScale = useYAxisScale();
  const area = usePlotArea();
  if (!center || !yScale || !area) return null;

  const points = changes
    .map((c) => ({ ...c, x: center(c.date), y: (yScale(c.from ?? c.to) + yScale(c.to ?? c.from)) / 2 }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  const groups = [];
  points.forEach((p) => {
    const last = groups[groups.length - 1];
    if (last && p.x - last[0].x < MERGE_PX) last.push(p);
    else groups.push([p]);
  });
  const goalY = goal ? yScale(goal) : null;
  // Merged changes get a numbered dot above the plot, clear of the bars and the line.
  const top = area.y - 12;
  const lastChange = changes[changes.length - 1];
  // The step went down: the ring is above the new line, so the value goes below it (and the other way up).
  // A numbered dot near the right end sits where the value would be, so the value goes below then too.
  const lastGroup = groups[groups.length - 1];
  const dotAtEnd = lastGroup && lastGroup.length > 1 && lastGroup[lastGroup.length - 1].x > area.x + area.width - 70;
  const valueBelow = (lastChange && lastChange.to < lastChange.from) || dotAtEnd;

  return (
    <g>
      {goalY != null && (
        <text x={area.x + area.width} y={valueBelow ? goalY + 18 : goalY - 8} textAnchor="end" fontSize={13} fontWeight={800}
          fill="var(--viz-goal-text)" stroke="var(--color-base-100)" strokeWidth={4} paintOrder="stroke">
          {kcal(goal)}
        </text>
      )}
      {groups.map((group) => {
        const first = group[0];
        const last = group[group.length - 1];
        const label = group.length === 1
          ? `Đổi mục tiêu ${first.text}`
          : `${group.length} lần đổi mục tiêu, xem chi tiết`;
        const activate = (e) => { e.stopPropagation(); onOpen({ items: group, x: last.x, y: group.length === 1 ? last.y : top }); };
        const keys = (e) => (e.key === 'Enter' || e.key === ' ') && activate(e);
        return (
          <g key={first.date} role="button" tabIndex={0} aria-label={label} onClick={activate} onKeyDown={keys} className="cursor-pointer outline-none">
            {group.map((p) => (
              <line key={p.date} x1={p.x} x2={p.x} y1={area.y} y2={area.y + area.height} stroke="var(--viz-axis)" strokeOpacity={0.6} strokeWidth={1.5} strokeDasharray="4 4" />
            ))}
            {group.length === 1 ? (
              <circle cx={first.x} cy={first.y} r={6} fill="var(--color-base-100)" stroke="var(--viz-goal-text)" strokeWidth={2.5} />
            ) : (
              <>
                <circle cx={last.x} cy={top} r={10} fill="var(--viz-goal-text)" stroke="var(--color-base-100)" strokeWidth={2} />
                <text x={last.x} y={top + 4} textAnchor="middle" fontSize={11} fontWeight={800} fill="var(--color-base-100)">{group.length}</text>
              </>
            )}
            {/* A bigger invisible target for fingers. */}
            <circle cx={last.x} cy={group.length === 1 ? last.y : top} r={18} fill="transparent" />
          </g>
        );
      })}
    </g>
  );
}

// Grey dots on the baseline for days with nothing logged; today, still empty, gets a dashed outline.
function EmptyDays({ rows }) {
  const center = useBandCenter(rows.map((r) => r.period));
  const yScale = useYAxisScale();
  if (!center || !yScale) return null;
  const step = Math.abs(center(rows[1].period) - center(rows[0].period));
  const base = yScale(0);
  const now = today();
  return (
    <g aria-hidden="true">
      {rows.filter((r) => !r.logged).map((r) => {
        const x = center(r.period);
        if (r.period === now) {
          const w = Math.min(28, step * 0.7);
          return <rect key={r.period} x={x - w / 2} y={base - 14} width={w} height={12} rx={3} fill="none" stroke="var(--viz-ok)" strokeDasharray="3 2" />;
        }
        return <circle key={r.period} cx={x} cy={base} r={step < 6 ? 1.2 : 2.2} fill="var(--viz-axis)" opacity={0.45} />;
      })}
    </g>
  );
}

// The tapped marker's changes, in a dark card over the chart.
function ChangeDetails({ open, width, onClose }) {
  if (!open) return null;
  const cardWidth = 230;
  const left = Math.max(4, Math.min(open.x - cardWidth / 2, width - cardWidth - 4));
  return (
    <div role="dialog" aria-label="Đổi mục tiêu" className="absolute z-10 rounded-[14px] bg-[#17201b] px-3 py-2.5 text-xs text-white shadow-lg"
      style={{ left, top: Math.max(0, open.y + 14), width: cardWidth }} onClick={onClose}>
      <div className="mb-1 text-[13px] font-bold">
        {open.items.length === 1 ? 'Đổi mục tiêu' : `${open.items.length} lần đổi mục tiêu`}
      </div>
      {open.items.map((c) => (
        (c.lines || [c.text]).map((text) => <div key={text} className="tabular">{text}</div>)
      ))}
    </div>
  );
}

function SwatchLegend({ items }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
      {items.map(([label, color]) => (
        <span key={label} className="flex items-center gap-1.5">
          <span className="inline-block size-2.5 rounded-[3px]" style={{ background: color }} /> {label}
        </span>
      ))}
    </div>
  );
}

// Axis labels: "Nay" for today; a week shows just the day numbers (the month goes under the axis),
// longer ranges "27/9".
const tickLabel = (iso, group, days) => {
  if (group === 'month') return formatPeriod(iso, group);
  if (iso === today()) return 'Nay';
  return days <= 7 ? String(parseISODate(iso).getDate()) : shortDate(iso);
};

// "Tháng 10", or "Tháng 9 – 10" when the week spans two months.
const monthCaption = (rows) => {
  const months = [...new Set(rows.map((r) => parseISODate(r.period).getMonth() + 1))];
  return months.length === 1 ? `Tháng ${months[0]}` : `Tháng ${months[0]} – ${months[months.length - 1]}`;
};

function formatPeriod(iso, group, long = false) {
  const date = parseISODate(iso);
  if (group === 'month') return date.toLocaleDateString(LOCALE, { month: long ? 'long' : 'short', year: long ? 'numeric' : undefined });
  return date.toLocaleDateString(LOCALE, long ? { weekday: 'short', day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short' });
}

function StatTile({ label, value, sub }) {
  return (
    <div className="rounded-[20px] bg-base-100 p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="text-xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted">{sub}</div>}
    </div>
  );
}

// Average per logged day; month rows are already daily averages, so weight them by days logged.
const averages = (rows, group) => {
  const logged = rows.filter((r) => r.logged);
  const weight = (r) => (group === 'month' ? r.days_logged : 1);
  const days = logged.reduce((sum, r) => sum + weight(r), 0);
  const avg = (key) => (days ? logged.reduce((sum, r) => sum + r[key] * weight(r), 0) / days : null);
  return { days, calories_in: avg('calories_in'), calories_out: avg('calories_out'), protein_g: avg('protein_g'), net: avg('net') };
};

// One dashed line per protein goal; rest and gym days only get separate lines when they differ. The lines
// are told apart by colour, named in a legend above the chart (labels on the lines overlap on phones).
// Each line follows the targets in force on each day, so it steps where the user changed a target; the
// legend shows the current values.
const proteinGoalLines = ({ protein_g: rest, workout_protein_g: workout }) => {
  if (rest && workout && rest !== workout) {
    return [{ y: rest, key: 'protein_goal', label: 'Ngày nghỉ', color: 'var(--viz-goal)' },
      { y: workout, key: 'workout_protein_goal', label: 'Ngày tập', color: 'var(--viz-goal-training)' }];
  }
  const goal = rest || workout;
  return goal ? [{ y: goal, key: rest ? 'protein_goal' : 'workout_protein_goal', label: 'Mục tiêu', color: 'var(--viz-goal)' }] : [];
};

function GoalLegend({ lines, format = grams }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
      {lines.map(({ y, label, color }) => (
        <span key={label} className="flex items-center gap-1.5">
          <svg width="18" height="4" aria-hidden="true"><line x1="0" y1="2" x2="18" y2="2" stroke={color} strokeWidth="2" strokeDasharray="4 3" /></svg>
          {label} {format(y)}
        </span>
      ))}
    </div>
  );
}

const proteinGoalText = (goals) => {
  const lines = proteinGoalLines(goals);
  if (lines.length === 2) return `Mục tiêu ${grams(lines[0].y)} nghỉ · ${grams(lines[1].y)} tập`;
  return lines.length ? `Mục tiêu ${grams(lines[0].y)}` : null;
};

export default function TrendsPage() {
  const { api } = useApp();
  const path = useAppPath();
  const [rangeId, setRangeId] = useState('30D');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const params = useMemo(() => rangeParams(RANGES.find((r) => r.id === rangeId)), [rangeId]);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    api.get(`/stats?${new URLSearchParams(params)}`)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [api, params]);

  // Unlogged periods become gaps rather than zeros.
  const rows = useMemo(
    () => (data?.series || []).map((r) => (r.logged ? r : { ...r, calories_in: null, calories_out: null, protein_g: null, net: null })),
    [data],
  );
  const stats = data ? averages(data.series, params.group) : null;
  const goals = data?.goals || {};
  const judged = (data?.series || []).filter((r) => r.logged && r.calorie_goal);
  const within = judged.filter(withinTarget);
  const allChanges = params.group === 'day' ? (data?.target_changes || []) : [];
  const changes = calorieMarks(allChanges);
  const proteinChanges = proteinMarks(allChanges);
  const [openChange, setOpenChange] = useState(null);
  const [chartWidth, setChartWidth] = useState(0);
  useEffect(() => setOpenChange(null), [rangeId]);
  // An open marker closes on the next tap anywhere (a tap on another marker then opens that one).
  useEffect(() => {
    if (!openChange) return undefined;
    const close = () => setOpenChange(null);
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [openChange]);
  const days = rows.length;
  const tick = (v) => tickLabel(v, params.group, days);
  // Clear space between bars, so two neighbouring days never read as one day's pair of columns.
  const barGap = days > 45 ? 1 : '30%';
  // About five labels, counted back from the last period so "Nay" (or this month) is always one of them.
  const every = Math.max(1, Math.ceil(days / 5));
  const ticks = rows.filter((_, i) => (days - 1 - i) % every === 0).map((r) => r.period);
  // With thin bars the last label sits at the very edge: end it there instead of centring it (clipped).
  const lastPeriod = rows[rows.length - 1]?.period;
  const tickProps = { ...axisProps, tick: ({ x, y, payload }) => (
    <text x={x} y={y + 12} textAnchor={days > 45 && payload.value === lastPeriod ? 'end' : 'middle'} fontSize={11} fontWeight={payload.value === today() ? 700 : 400}
      fill={payload.value === today() ? 'var(--viz-goal-text)' : 'var(--viz-axis)'}>{tick(payload.value)}</text>
  ) };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="flex items-center gap-2 text-[22px] font-bold">
        <span className="h-6 w-1.5 rounded-full bg-[#8e72d8]" aria-hidden="true" /> Thống kê
      </h1>
      <AnnouncementBanner screen="trends" />
      <div role="tablist" className="tabs tabs-box tabs-sm justify-between">
        {RANGES.map((r) => (
          <button key={r.id} type="button" role="tab" className={`tab flex-1 whitespace-nowrap px-1 ${rangeId === r.id ? 'tab-active' : ''}`}
            onClick={() => setRangeId(r.id)}>
            {r.label}
          </button>
        ))}
      </div>

      {error && <div role="alert" className="alert alert-error">{error}</div>}
      {!data && !error && <TrendsSkeleton />}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <StatTile label="TB đã ăn / ngày" value={`${kcal(stats.calories_in)} kcal`}
              sub={goals.calories ? `Mục tiêu ${kcal(goals.calories)}` : null} />
            <StatTile label="TB đạm / ngày" value={grams(stats.protein_g)} sub={proteinGoalText(goals)} />
            <StatTile label="TB đã đốt / ngày" value={`${kcal(stats.calories_out)} kcal`} />
            <StatTile label="Số ngày đã ghi" value={stats.days} sub={`TB ròng ${kcal(stats.net)} kcal`} />
          </div>

          <section className="card rounded-[20px] bg-base-100">
            <div className="card-body gap-3 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-semibold">Calo ròng{params.group === 'month' ? ' (TB mỗi ngày)' : ''}</h2>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
                  <SwatchLegend items={[['Trong mục tiêu', 'var(--viz-ok)'], ['Vượt', 'var(--viz-over)']]} />
                  {goals.calories && (
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block h-[3px] w-4 rounded" style={{ background: 'var(--viz-goal-text)' }} /> Mục tiêu
                    </span>
                  )}
                </div>
              </div>
              <div className="relative -ml-3">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" onResize={(w) => setChartWidth(w)}>
                    <ComposedChart data={rows} margin={{ top: 22, right: 8, bottom: 0, left: 0 }} barCategoryGap={barGap}>
                      <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
                      <XAxis dataKey="period" {...tickProps} ticks={ticks} interval={0} />
                      <YAxis {...axisProps} width={44} tickFormatter={kcal} />
                      <Tooltip content={<CalorieTooltip group={params.group} />} cursor={{ fill: 'var(--viz-grid)', opacity: 0.5 }}
                        active={openChange?.chart === 'calories' ? false : undefined} />
                      <Bar dataKey="net" radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false}>
                        {rows.map((r) => (
                          <Cell key={r.period} fillOpacity={r.period === today() ? 0.45 : 1}
                            fill={!r.calorie_goal ? SERIES.calories_in.color : withinTarget(r) ? 'var(--viz-ok)' : 'var(--viz-over)'} />
                        ))}
                      </Bar>
                      {goals.calories && (
                        <Line dataKey="calorie_goal" type="stepAfter" stroke="var(--viz-goal-text)" strokeWidth={2.5}
                          dot={false} activeDot={false} isAnimationActive={false} connectNulls />
                      )}
                      {/* Above the bars and the target line, so the markers can be tapped. */}
                      <ZIndexLayer zIndex={1300}>
                        {params.group === 'day' && <EmptyDays rows={rows} />}
                        <ChangeMarkers periods={rows.map((r) => r.period)} changes={changes} goal={goals.calories}
                          onOpen={(o) => setOpenChange({ ...o, chart: 'calories' })} />
                      </ZIndexLayer>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <ChangeDetails open={openChange?.chart === 'calories' && openChange} width={chartWidth} onClose={() => setOpenChange(null)} />
                {params.group === 'day' && days <= 7 && <div className="-mt-1 text-center text-xs text-muted">{monthCaption(rows)}</div>}
              </div>
              {(judged.length > 0 || changes.length > 0) && (
                <div className="flex flex-col gap-2 border-t border-line pt-3">
                  {judged.length > 0 && (
                    <div className="flex items-baseline justify-between text-sm">
                      <span>Trong mục tiêu</span>
                      <b className="tabular">
                        {within.length}/{judged.length} {params.group === 'month' ? 'tháng có ghi' : 'ngày có ghi'}
                      </b>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="flex items-center gap-2">
                      {changes.length > 0 && (
                        <span aria-hidden="true" className="inline-block size-3 rounded-full border-[2.5px] border-[var(--viz-goal-text)]" />
                      )}
                      {changes.length > 0
                        ? `${changes.length} lần đổi mục tiêu trong ${RANGES.find((r) => r.id === rangeId).label}`
                        : 'Không đổi mục tiêu trong giai đoạn này'}
                    </span>
                    <Link to={path('/targets')} className="shrink-0 font-semibold text-brand-text">Xem lịch sử</Link>
                  </div>
                  <p className="text-xs text-muted">
                    Calo ròng = đã ăn − vận động. Mỗi {params.group === 'month' ? 'tháng' : 'ngày'} được so với mục tiêu của chính {params.group === 'month' ? 'tháng' : 'ngày'} đó.
                    {params.group === 'day' && ' Chấm xám là ngày chưa ghi.'}
                  </p>
                </div>
              )}
            </div>
          </section>

          <section className="card rounded-[20px] bg-base-100">
            <div className="card-body gap-3 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-semibold">Chất đạm (g){params.group === 'month' ? ' (TB mỗi ngày)' : ''}</h2>
                <GoalLegend lines={proteinGoalLines(goals)} />
              </div>
              <div className="relative -ml-3">
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={rows} margin={{ top: proteinChanges.length ? 22 : 8, right: 8, bottom: 0, left: 0 }} barCategoryGap={barGap}>
                      <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
                      <XAxis dataKey="period" {...tickProps} ticks={ticks} interval={0} />
                      <YAxis {...axisProps} width={44} />
                      {proteinGoalLines(goals).map(({ key, label, color }) => (
                        <Line key={label} dataKey={key} type="stepAfter" stroke={color} strokeWidth={1.5} strokeDasharray="4 4"
                          dot={false} activeDot={false} isAnimationActive={false} connectNulls />
                      ))}
                      <Tooltip content={<ChartTooltip group={params.group} />} cursor={{ fill: 'var(--viz-grid)', opacity: 0.5 }}
                        active={openChange?.chart === 'protein' ? false : undefined} />
                      <Bar dataKey="protein_g" fill={SERIES.protein_g.color} radius={[4, 4, 0, 0]} maxBarSize={28}
                        isAnimationActive={false} />
                      <ZIndexLayer zIndex={1300}>
                        <ChangeMarkers periods={rows.map((r) => r.period)} changes={proteinChanges}
                          onOpen={(o) => setOpenChange({ ...o, chart: 'protein' })} />
                      </ZIndexLayer>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <ChangeDetails open={openChange?.chart === 'protein' && openChange} width={chartWidth} onClose={() => setOpenChange(null)} />
              </div>
              {proteinChanges.length > 0 && (
                <div className="flex items-center justify-between gap-3 border-t border-line pt-3 text-sm">
                  <span className="flex items-center gap-2">
                    <span aria-hidden="true" className="inline-block size-3 shrink-0 rounded-full border-[2.5px] border-[var(--viz-goal-text)]" />
                    {proteinChanges.length} lần đổi mục tiêu đạm trong {RANGES.find((r) => r.id === rangeId).label}
                  </span>
                  <Link to={path('/targets')} className="shrink-0 font-semibold text-brand-text">Xem lịch sử</Link>
                </div>
              )}
            </div>
          </section>

          <details className="collapse collapse-arrow rounded-[20px] bg-base-100">
            <summary className="collapse-title font-semibold">Xem dạng bảng</summary>
            <div className="collapse-content overflow-x-auto">
              <table className="table table-sm tabular">
                <thead>
                  <tr><th>{params.group === 'month' ? 'Tháng' : 'Ngày'}</th><th>Đã ăn</th><th>Đã đốt</th><th>Ròng</th><th>Đạm</th></tr>
                </thead>
                <tbody>
                  {[...data.series].reverse().filter((r) => r.logged).map((r) => (
                    <tr key={r.period}>
                      <td>{formatPeriod(r.period, params.group)}</td>
                      <td>{kcal(r.calories_in)}</td>
                      <td>{kcal(r.calories_out)}</td>
                      <td>{kcal(r.net)}</td>
                      <td>{grams(r.protein_g)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </div>
  );
}
